import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  TIME_ENTRY_STATUSES,
  formatDate,
  formatDuration,
  lastWeekBounds,
  runningSeconds,
  timeEntryStatusLabel,
  type Paginated,
} from '@codepass/shared';
import { ApiError, api } from '@/lib/api';
import {
  Alert,
  Button,
  Card,
  Combobox,
  Input,
  MultiSelect,
  Skeleton,
  Table,
  Td,
  Th,
  Tr,
} from '@/components/ui';
import {
  ConsultantCell,
  EmptyState,
  FilterField,
  FilterPanel,
  PageHeader,
  Pagination,
  TimeEntryStatusBadge,
} from '@/components/domain';
import { SubtaskModal } from '@/components/SubtaskModal';
import type { TimeEntryFilterOptions, TimeEntryItem, TimeEntryRunning } from '@/types';

const LIMIT = 50;

/**
 * Sonden over igangvaerende timere spoerges hvert sekund. Den er billig nok til det - tre
 * kolonner fra en haandfuld raekker - og den er det eneste der skal koere hurtigt.
 *
 * Selve listen hentes kun paa et halvt minut som sikkerhedsnet, plus naar sonden siger at
 * billedet er forældet. Foer sonden fandtes, var kadencen bundet til om der *allerede* var
 * noget i gang, og saa blev et start foerst opdaget efter et halvt minut, mens et stop blev
 * fanget paa et sekund. Sonden fjerner den skaevhed: baade start og stop ses med det samme.
 */
const RUNNING_PROBE_MS = 1_000;
const REFETCH_LIST_MS = 30_000;

/**
 * Hvor ofte sekundvisningen genberegnes.
 *
 * Et helt sekund er for langsomt: timerne er startet paa hver sit tidspunkt inde i
 * sekundet (fx .611), saa et tick én gang i sekundet rammer skiftet med en vilkaarlig
 * forsinkelse - i vaerste fald et helt sekund. Fire gange i sekundet holder afvigelsen
 * under et kvart sekund for alle raekker, uanset hvornaar de er startet.
 */
const RUNNING_TICK_MS = 250;

/**
 * Timeoversigten. Kolonnerne foelger PRD par. 6.1 "Tabel-kolonner".
 *
 * Kilden er OnePro's opgaver med deres akkumulerede tid, ikke Codepass' egne timeraekker
 * - se kommentaren i apps/api/src/routes/time-entries.ts. Derfor er der heller ingen
 * bulk-godkendelse her endnu: der findes ingen validerings-status at aendre paa en
 * OnePro-opgave. Handlinger-kolonnen viser underopgaverne i stedet.
 */
function useTimeEntries(filters: Record<string, string | number | undefined>) {
  return useQuery({
    queryKey: ['time-entries', filters],
    queryFn: () => api.get<Paginated<TimeEntryItem>>('/time-entries', filters),
    /** Sikkerhedsnet. Aendringer opdages af sonden, ikke af dette interval. */
    refetchInterval: REFETCH_LIST_MS,
    refetchIntervalInBackground: true,

    /**
     * Overstyrer standarden i main.tsx (30 sekunders staleTime, ingen hentning ved fokus).
     *
     * En timer startes og stoppes i OnePro - altsaa i et andet vindue - og imens ligger
     * Codepass i baggrunden, hvor browseren struber setInterval. Naar fanen faar fokus
     * igen, er det praecis det oejeblik brugeren kigger paa tallet, og saa skal det passe.
     *
     * staleTime skal vaere 0, ellers springer hentningen ved fokus over: den henter kun
     * data der regnes for foraeldede, og med standardens 30 sekunder ville en raekke
     * kunne staa og taelle videre paa en start_time der ikke gaelder mere.
     */
    staleTime: 0,
    refetchOnWindowFocus: true,
    /** Den forrige side bliver staaende mens den naeste hentes - ingen blinkende tabel. */
    placeholderData: (previous) => previous,
  });
}

/**
 * De timere der koerer lige nu, spurgt hvert sekund.
 *
 * Ligger uden for listens query-noegle med vilje: den er den samme uanset hvilke filtre
 * der er valgt, saa et skift af filter skal ikke starte forfra paa den.
 */
function useRunningTimers() {
  return useQuery({
    queryKey: ['time-entries-running'],
    queryFn: () => api.get<{ data: TimeEntryRunning[] }>('/time-entries/running'),
    refetchInterval: RUNNING_PROBE_MS,
    refetchIntervalInBackground: true,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

/** Dropdownenes indhold. Skifter sjaeldent, saa den maa gerne ligge laenge i cachen. */
function useFilterOptions() {
  return useQuery({
    queryKey: ['time-entry-filters'],
    queryFn: () => api.get<TimeEntryFilterOptions>('/time-entries/filters'),
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Et ur der gaar, men kun saa laenge mindst én timer koerer.
 *
 * Uden betingelsen ville hver eneste side gen-rendere én gang i sekundet resten af dagen,
 * uden at noget paa skaermen aendrede sig.
 */
function useTick(active: boolean): Date {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => setNow(new Date()), RUNNING_TICK_MS);
    return () => clearInterval(timer);
  }, [active]);

  return now;
}

/** Underopgaverne bag Handlinger-knappen. Selve vinduet deles med Timer-fanen paa en kontrakt. */
function TimeEntryTasksModal({ entry, onClose }: { entry: TimeEntryItem; onClose: () => void }) {
  return (
    <SubtaskModal
      todoId={entry.id}
      title={entry.title}
      subtitle={[entry.contract_label, entry.customer?.name].filter(Boolean).join(' · ')}
      onClose={onClose}
    />
  );
}

/**
 * Udgangstilstanden for filtrene. Bruges baade naar siden aabner og af "Ryd filtre".
 *
 * **Perioden er sat som standard, ikke tom:** den seneste uge til og med i dag. Siden aabnede
 * foer paa *alle* timer OnePro har registreret - knap 4.000 opgaver - og det er sjaeldent
 * spoergsmaalet. Den uge der lige er gaaet, er det, man kommer for. Se `lastWeekBounds` for
 * hvorfor `to` er i dag og ikke i gaar.
 *
 * En funktion og ikke en konstant: "i dag" maa afgoeres naar siden aabner, ikke da modulet
 * blev indlaest. Perioden taeller derfor heller ikke som et filter man selv har sat - "Ryd
 * filtre" fører tilbage hertil, og knappen er slaaet fra, indtil noget faktisk er aendret.
 */
function defaultFilters(now: Date) {
  const period = lastWeekBounds(now);

  return {
    statuses: [] as string[],
    consultantId: '',
    /** Kontraktnummer, eller CONTRACT_FILTER_OTHER for de opgaver der ikke har en aftale. */
    contract: '',
    customerId: '',
    invert: false,
    search: '',
    from: period.start,
    to: period.end,
  };
}

type FilterState = ReturnType<typeof defaultFilters>;

/**
 * Timeoversigten: alt hvad OnePro har registreret, uden anden afgraensning end filtrene.
 *
 * Den eneste side under /timer. Undersiderne er faldet én ad gangen - Afsluttede, Godkendte,
 * Afviste og til sidst Timekladde - fordi de alle var den her tabel med ét fast filter, og
 * fordi beslutningen bag Godkendt og Afvist blev truffet i OnePro og ikke her. Statusfilteret
 * i venstre side daekker det, de kunne.
 */
function TimeEntriesPage() {
  const [offset, setOffset] = useState(0);
  /* Én gang pr. besoeg: "i dag" maa ikke flytte sig, mens siden er aaben. */
  const defaults = useMemo(() => defaultFilters(new Date()), []);
  const [filters, setFilters] = useState<FilterState>(defaults);
  const [openEntry, setOpenEntry] = useState<TimeEntryItem | null>(null);

  /** Et aendret filter goer den aktuelle side meningsloes - start forfra. */
  const update = (patch: Partial<FilterState>): void => {
    setFilters((current) => ({ ...current, ...patch }));
    setOffset(0);
  };

  const query = useMemo(
    () => ({
      status: filters.statuses.length > 0 ? filters.statuses.join(',') : undefined,
      consultant_id: filters.consultantId || undefined,
      contract_no: filters.contract || undefined,
      workspace_id: filters.customerId || undefined,
      invert: filters.invert ? 'true' : undefined,
      search: filters.search || undefined,
      from: filters.from || undefined,
      to: filters.to || undefined,
      limit: LIMIT,
      offset,
    }),
    [filters, offset],
  );

  const { data, isLoading, error } = useTimeEntries(query);
  const options = useFilterOptions();
  const running = useRunningTimers();
  const queryClient = useQueryClient();

  const fetched = useMemo(() => data?.data ?? [], [data]);

  const live = useMemo(
    () => new Map((running.data?.data ?? []).map((timer) => [timer.id, timer])),
    [running.data],
  );

  /**
   * Sonden er nyere end listen, saa den vinder om hvorvidt en raekke koerer.
   *
   * Kun i den ene retning: siger sonden at en raekke koerer, kan den vises som koerende
   * med det samme - den baerer selv baade det akkumulerede og starttidspunktet. Siger den
   * derimod at en koerende raekke er stoppet, kender kun listen den nye total_time, saa
   * dér ventes der paa hentningen nedenfor frem for at vise et forældet tal.
   */
  const rows = useMemo(
    () =>
      fetched.map((entry) => {
        const timer = live.get(entry.id);
        if (!timer) return entry;
        return {
          ...entry,
          is_running: true,
          duration_seconds: timer.duration_seconds,
          started_at: timer.started_at,
        };
      }),
    [fetched, live],
  );

  /**
   * Naar sonden og listen er uenige om hvad der koerer, er listen forældet - en timer er
   * lige startet eller stoppet i OnePro. Hent den igen med det samme.
   *
   * Sammenligningen sker kun paa de raekker listen faktisk viser. Ellers ville en timer
   * paa en opgave uden for filteret eller siden goere de to maengder evigt uenige, og
   * listen ville blive hentet igen hvert sekund uden at noget aendrede sig.
   */
  useEffect(() => {
    if (!running.data) return;

    const disagrees = fetched.some((entry) => entry.is_running !== live.has(entry.id));
    if (disagrees) void queryClient.invalidateQueries({ queryKey: ['time-entries'] });
  }, [fetched, live, running.data, queryClient]);

  const now = useTick(rows.some((entry) => entry.is_running));

  const isFiltered = JSON.stringify(filters) !== JSON.stringify(defaults);

  return (
    <>
      <PageHeader
        title="Timer"
        description="Registreret tid pr. opgave i OnePro. Igangværende timere opdateres af sig selv."
      />

      <div className="flex flex-col gap-4 lg:flex-row">
        <aside className="w-full shrink-0 lg:w-[280px]">
          <FilterPanel
            title={<span className="text-xs font-semibold text-ink-muted">Filtre</span>}
            active={isFiltered}
            action={
              <Button
                size="sm"
                variant="ghost"
                disabled={!isFiltered}
                onClick={() => {
                  setFilters(defaults);
                  setOffset(0);
                }}
              >
                Ryd filtre
              </Button>
            }
          >
            <FilterField label="Status">
              <MultiSelect
                values={filters.statuses}
                onChange={(statuses) => update({ statuses })}
                options={TIME_ENTRY_STATUSES.map((option) => ({
                  value: option,
                  label: timeEntryStatusLabel(option),
                }))}
                allLabel="Alle statusser"
              />
            </FilterField>

            <FilterField label="Konsulent">
              <Combobox
                value={filters.consultantId}
                onChange={(value) => update({ consultantId: value })}
                options={(options.data?.consultants ?? []).map((row) => ({
                  value: row.id,
                  label: row.label,
                }))}
                allLabel="Alle konsulenter"
                searchPlaceholder="Søg konsulent"
              />
            </FilterField>

            <FilterField label="Kontrakt">
              <Combobox
                value={filters.contract}
                onChange={(value) => update({ contract: value })}
                // Kun aktive aftaler, navngivet "ONC-1011 · Keymate Portal" og med "Andet"
                // sidst. Raekkefoelgen kommer fra /time-entries/filters og maa ikke sorteres
                // om her.
                options={options.data?.contracts ?? []}
                allLabel="Alle kontrakter"
                searchPlaceholder="Søg kontrakt"
              />
            </FilterField>

            <FilterField label="Kunde">
              <Combobox
                value={filters.customerId}
                onChange={(value) => update({ customerId: value })}
                options={(options.data?.customers ?? []).map((row) => ({
                  value: row.id,
                  label: row.label,
                }))}
                allLabel="Alle kunder"
                searchPlaceholder="Søg kunde"
              />
            </FilterField>

            <label className="flex items-start gap-2 rounded-md border border-line p-2 text-xs">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={filters.invert}
                onChange={(event) => update({ invert: event.target.checked })}
              />
              <span>
                <span className="font-medium">Ikke matchet</span>
                <span className="mt-0.5 block text-ink-muted">
                  Vender status, konsulent, kontrakt og kunde om. Vælg fx dig selv for at se de
                  timer, du <em>ikke</em> har arbejdet på.
                </span>
              </span>
            </label>

            <FilterField label="Søg i opgave">
              <Input
                placeholder="Fx: fakturamotor"
                value={filters.search}
                onChange={(event) => update({ search: event.target.value })}
              />
            </FilterField>

            <FilterField label="Fra dato">
              <Input
                type="date"
                value={filters.from}
                max={filters.to || undefined}
                onChange={(event) => update({ from: event.target.value })}
              />
            </FilterField>

            <FilterField label="Til dato">
              {/*
                min binder til-datoen til fra-datoen, saa et interval der slutter foer det
                begynder ikke kan vaelges. Serveren afviser det ogsaa - se listQuerySchema
                - men datovaelgeren skal ikke lade brugeren naa dertil.
              */}
              <Input
                type="date"
                value={filters.to}
                min={filters.from || undefined}
                onChange={(event) => update({ to: event.target.value })}
              />
            </FilterField>
          </FilterPanel>
        </aside>

        <div className="min-w-0 flex-1">
          <Card>
            {isLoading ? (
              <div className="space-y-2 p-4">
                {[0, 1, 2, 3, 4].map((index) => (
                  <Skeleton key={index} className="h-10" />
                ))}
              </div>
            ) : error ? (
              <Alert tone="error" className="m-4">
                {error instanceof ApiError ? error.message : 'Kunne ikke hente timer.'}
              </Alert>
            ) : rows.length === 0 ? (
              <EmptyState
                title="Ingen timer matcher filtrene"
                /*
                 * Perioden er sat som standard, og det maa siges her: uden det ville en tom
                 * tabel se ud som om der slet ikke er registreret timer, mens den i
                 * virkeligheden kun daekker den seneste uge.
                 */
                description={
                  isFiltered
                    ? undefined
                    : `Perioden er som standard ${formatDate(filters.from)} – ` +
                      `${formatDate(filters.to)}. Ret datoerne for at se andre timer.`
                }
                action={
                  isFiltered ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setFilters(defaults);
                        setOffset(0);
                      }}
                    >
                      Ryd filtre
                    </Button>
                  ) : undefined
                }
              />
            ) : (
              <>
                <Table>
                  <thead>
                    <tr>
                      <Th className="w-[110px]">Dato</Th>
                      <Th className="w-[190px]">Konsulent</Th>
                      <Th>Kontrakt</Th>
                      <Th className="w-[160px]">Kunde</Th>
                      <Th className="w-[120px] text-right">Timer</Th>
                      <Th>Beskrivelse</Th>
                      <Th className="w-[150px]">Status</Th>
                      <Th className="w-[140px]">Handlinger</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((entry) => (
                      // Opgavens navn staar i Beskrivelse-kolonnen, men den beskaeres naar
                      // navnet er langt. Tooltippet paa raekken viser det fulde navn,
                      // uanset hvor paa raekken musen er.
                      <Tr key={entry.id} title={entry.title ?? undefined}>
                        <Td className="tabular">{formatDate(entry.created_at)}</Td>
                        <Td className="text-xs">
                          <ConsultantCell consultant={entry.consultant} />
                        </Td>
                        <Td className="text-xs">{entry.contract_label ?? '—'}</Td>
                        <Td className="text-xs">{entry.customer?.name ?? '—'}</Td>
                        <Td className="text-right tabular">
                          <RunningDuration entry={entry} now={now} />
                        </Td>
                        <Td
                          className="max-w-[260px] truncate text-xs text-ink-muted"
                          title={entry.title ?? undefined}
                        >
                          {entry.title ?? '—'}
                        </Td>
                        <Td>
                          <TimeEntryStatusBadge status={entry.status} />
                        </Td>
                        <Td>
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={entry.task_count === 0}
                            title={
                              entry.task_count === 0
                                ? 'Ingen underopgaver på denne opgave'
                                : undefined
                            }
                            onClick={() => setOpenEntry(entry)}
                          >
                            Vis opgaver{entry.task_count > 0 ? ` (${entry.task_count})` : ''}
                          </Button>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
                <Pagination
                  total={data?.total ?? 0}
                  limit={LIMIT}
                  offset={offset}
                  onChange={setOffset}
                />
              </>
            )}
          </Card>
        </div>
      </div>

      {openEntry ? (
        <TimeEntryTasksModal entry={openEntry} onClose={() => setOpenEntry(null)} />
      ) : null}
    </>
  );
}

/**
 * Timetallet. Koerer timeren, taelles den igangvaerende koersel med, og raekken faar en
 * prik - farven alene ville ikke fortaelle en skaermlaeser at uret gaar.
 */
function RunningDuration({ entry, now }: { entry: TimeEntryItem; now: Date }) {
  if (!entry.is_running) return <>{formatDuration(entry.duration_seconds)}</>;

  return (
    <span
      className="inline-flex items-center gap-1.5 font-medium text-brand-800"
      title="Timeren kører i OnePro"
    >
      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand" aria-hidden />
      {formatDuration(runningSeconds(entry.duration_seconds, entry.started_at, now))}
      <span className="sr-only">(kører)</span>
    </span>
  );
}

export function HoursPage() {
  return <TimeEntriesPage />;
}
